/* eslint-disable import/no-cycle */
/* eslint-disable react-hooks/rules-of-hooks */

import i18next from 'i18next';
import dayjs from 'dayjs';

import logoSeguritech from 'assets/SeguriTech';
import { Client, ClientLocationDetail, ClientService } from 'api/client';
import {
  GetClosedCommentsToTicket,
  GetCommentListByTicketResponse,
  IReportConfig,
  TicketDetail
} from 'api/ticket';
import { ProductForTicket } from 'api/product';
import { AccesoryResponse, SupplyResponse } from 'api/inventory';
import { removeEmojis } from 'helpers/utils';

import * as pdfMakeUtils from '../pdfMakeUtils';
import getIncidentOrderInfoTable from './getIncidentOrderInfoTable';
import getIncidentDescriptionTable from './getIncidentDescriptionTable';
// import getSiteSupportTable from './getSiteSupportTable';

export interface ITicketDetail extends TicketDetail {
  ticketProduct: ProductForTicket[];
}

export type TicketData = {
  clientActive: Client;
  projectAndService: ClientService;
  dataIncident: ITicketDetail;
  location: ClientLocationDetail;
  supplies: SupplyResponse[];
  installedAccesories: AccesoryResponse[];
  comments: GetCommentListByTicketResponse[];
  closedTicketComments?: GetClosedCommentsToTicket;
  reportConfig?: IReportConfig;
};

const getPdfTemplate = (
  ticketData: TicketData,
  pdfImages: string[],
  imageDescription: string[],
  logo?: string
) => {
  const pageWidth = 595;

  const images = pdfImages?.reduce(
    (acc, item) => ({
      ...acc,
      [item]: item
    }),
    {}
  );

  const clientName = () => {
    if (!ticketData?.reportConfig?.client) return '';
    if (typeof ticketData?.reportConfig?.client === 'string') {
      return ticketData?.reportConfig?.client;
    }
    return ticketData?.reportConfig?.client?.label || '';
  };

  const requestedByUser = () => {
    if (!ticketData?.reportConfig?.requested) return '';
    if (typeof ticketData?.reportConfig?.requested === 'string') {
      return ticketData?.reportConfig?.requested;
    }
    return ticketData?.reportConfig?.requested?.label || '';
  };

  const imagesRows = pdfImages?.map((image, index) => [
    {
      image,
      width: pageWidth * 0.4,
      alignment: 'center'
    },
    {
      text: imageDescription[index] || '',
      alignment: 'center',
      style: 'titleBold',
      margin: [0, 10, 0, 0]
    }
  ]);

  const chunk = (arr: [], len: number) => {
    const chunks = [];
    let i = 0;
    const n = arr?.length;

    while (i < n) {
      chunks?.push(arr?.slice(i, (i += len)));
    }

    return chunks;
  };

  const { pdfMake } = window;

  pdfMake.tableLayouts = {
    ...pdfMake.tableLayouts,
    customTheme: pdfMakeUtils.customTheme.layout,
    borderedContainer: pdfMakeUtils.borderedContainer,
    tableBorderedContainer: pdfMakeUtils.tableBorderedContainer
  };

  const content = {
    pageSize: 'A4',
    pageMargins: [20, 40, 20, 40],
    defaultStyle: pdfMakeUtils.customTheme.defaultStyle,
    styles: pdfMakeUtils.customTheme.styles,
    footer: () => ({
      text: `${i18next.t('ticket.properties.footerPDFLabel', {
        ns: 'global'
      })}`,
      alignment: 'right',
      fontSize: 7, // Tamaño de fuente directamente aquí
      margin: [0, 0, 20, 0]
    }),
    header: (currentPage: number) => {
      if (currentPage > 1) {
        return {
          columns: [
            {
              text: '',
              width: pageWidth * 0.2
            },
            {
              text: '',
              width: '45%'
            },
            {
              text: `${i18next.t('costReport.columns.ticketID', {
                ns: 'global'
              })}: ${ticketData?.dataIncident?.ticketId || ''}`,
              style: 'titleRight',
              width: 'auto'
            }
          ],
          margin: [0, 20]
        };
      }
      return null;
    },
    content: [
      {
        columns: [
          {
            image:
              ticketData?.clientActive?.blobStorageUrlPath && logo
                ? `${logo}`
                : `data:image/png;base64,${logoSeguritech}`,
            width: pageWidth * 0.2
          },
          {
            text: `${i18next.t('ticket.pdf.order', {
              ns: 'global'
            })}\n${ticketData?.dataIncident?.ticketServiceName || ''}`,
            style: 'titleBold',
            width: '55%'
          },

          {
            text: `${i18next.t('costReport.columns.ticketID', {
              ns: 'global'
            })}: ${ticketData?.dataIncident?.ticketId || ''}`,
            style: 'titleRight',
            width: '25%',
            margin: [0, 0, 0, 50]
          }
        ]
      },
      ' ',
      getIncidentOrderInfoTable(ticketData),
      ' ',
      getIncidentDescriptionTable(ticketData),
      ' ',
      {
        table: {
          widths: ['*', '*'],
          body: [
            [
              pdfMakeUtils.BorderedCell(
                `${i18next.t('common.commonProperties.invoice', {
                  ns: 'global'
                })}:\n${ticketData?.dataIncident?.ticketId || ''}`,
                40
              ),
              pdfMakeUtils.BorderedCell(
                `${i18next.t('common.commonProperties.taskForce', {
                  ns: 'global'
                })}:\n${
                  ticketData?.dataIncident?.ticketAssignedEngineerFullName || ''
                }`,
                40
              )
            ],
            [
              pdfMakeUtils.BorderedCell(
                `${i18next.t('ticket.pdf.siteDate', {
                  ns: 'global'
                })}:\n${
                  ticketData?.dataIncident?.ticketWorkingDateTime
                    ? dayjs
                        .utc(ticketData?.dataIncident?.ticketWorkingDateTime)
                        .local()
                        .format('DD/MM/YYYY')
                    : ''
                }`,
                40
              ),
              pdfMakeUtils.BorderedCell(
                `${i18next.t('ticket.pdf.siteTime', {
                  ns: 'global'
                })}:\n${
                  ticketData?.dataIncident?.ticketWorkingDateTime
                    ? dayjs
                        .utc(ticketData?.dataIncident?.ticketWorkingDateTime)
                        .local()
                        .format('HH:mm:ss')
                    : ''
                }`,
                40
              )
            ],
            [
              pdfMakeUtils.BorderedCell(
                `${i18next.t('ticket.pdf.closeDate', {
                  ns: 'global'
                })}:\n${
                  ticketData?.dataIncident?.ticketClosedDateTime
                    ? dayjs
                        .utc(ticketData?.dataIncident?.ticketClosedDateTime)
                        .local()
                        .format('DD/MM/YYYY')
                    : ''
                }`,
                40
              ),
              pdfMakeUtils.BorderedCell(
                `${i18next.t('ticket.pdf.closeTime', {
                  ns: 'global'
                })}:\n${
                  ticketData?.dataIncident?.ticketClosedDateTime
                    ? dayjs
                        .utc(ticketData?.dataIncident?.ticketClosedDateTime)
                        .local()
                        .format('HH:mm:ss')
                    : ''
                }`,
                40
              )
            ]
          ]
        },
        layout: 'borderedContainer',
        pageBreak: 'after'
      },
      ' ',
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.closeComments', {
                  ns: 'global'
                })}`,
                style: 'titleBold'
              }
            ],
            [
              ticketData.closedTicketComments?.commentDetails.split('\n')
                ?.length > 0
                ? [
                    {
                      ul: ticketData.closedTicketComments?.commentDetails
                        .split('\n')
                        .map((comment) => removeEmojis(comment))
                    }
                  ]
                : [
                    `${i18next.t('ticket.pdf.noComment', {
                      ns: 'global'
                    })}`
                  ]
            ]
          ]
        },
        layout: 'borderedContainer'
      },
      ' ',
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.comment', {
                  ns: 'global'
                })}`,
                style: 'titleBold'
              }
            ],
            ticketData.comments?.length > 0
              ? [
                  {
                    ul: ticketData.comments?.map((comment) =>
                      removeEmojis(comment?.comment)
                    )
                  }
                ]
              : [
                  {
                    ul: [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ]
                  }
                ]
          ]
        },
        layout: 'borderedContainer'
      },
      ' ',
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.totalComponents', {
                  ns: 'global'
                })}`,
                style: 'titleBold'
              }
            ],
            [
              `${i18next.t('ticket.pdf.installed', {
                ns: 'global'
              })}`
            ],
            ticketData.dataIncident?.ticketProduct &&
            ticketData.dataIncident?.ticketProduct?.filter(
              (p: ProductForTicket) => p?.productConditionId === 1
            )?.length > 0
              ? [
                  {
                    ul: ticketData.dataIncident?.ticketProduct
                      ?.filter(
                        (p: ProductForTicket) => p?.productConditionId === 1
                      )
                      ?.map(
                        (product: ProductForTicket) =>
                          `${i18next.t('common.commonProperties.serialn', {
                            ns: 'global'
                          })}: ${product?.serialNumber || ''} - ${i18next.t(
                            'common.commonProperties.description',
                            {
                              ns: 'global'
                            }
                          )}: ${product?.modelDescription || ''} - ${i18next.t(
                            'common.commonProperties.model',
                            {
                              ns: 'global'
                            }
                          )}: ${product?.modelName || ''} - ${i18next.t(
                            'common.commonProperties.brand',
                            {
                              ns: 'global'
                            }
                          )}: ${product?.brandName || ''}`
                      )
                  }
                ]
              : [
                  {
                    ul: [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ]
                  }
                ],
            [
              `${i18next.t('ticket.pdf.retired', {
                ns: 'global'
              })}`
            ],
            ticketData.dataIncident?.ticketProduct &&
            ticketData.dataIncident?.ticketProduct?.filter(
              (p: ProductForTicket) => p?.productConditionId === 4
            )?.length > 0
              ? [
                  {
                    ul: ticketData.dataIncident?.ticketProduct
                      ?.filter(
                        (p: ProductForTicket) => p?.productConditionId === 4
                      )
                      ?.map(
                        (product: ProductForTicket) =>
                          `${i18next.t('common.commonProperties.serialn', {
                            ns: 'global'
                          })}: ${product?.serialNumber || ''} - ${i18next.t(
                            'common.commonProperties.description',
                            {
                              ns: 'global'
                            }
                          )}: ${product?.modelDescription || ''} - ${i18next.t(
                            'common.commonProperties.model',
                            {
                              ns: 'global'
                            }
                          )}: ${product?.modelName || ''} - ${i18next.t(
                            'common.commonProperties.brand',
                            {
                              ns: 'global'
                            }
                          )}: ${product?.brandName || ''}`
                      )
                  }
                ]
              : [
                  {
                    ul: [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ]
                  }
                ]
          ]
        },
        layout: 'borderedContainer'
      },
      ' ',
      {
        table: {
          widths: ['*', '*'],
          heights: 100,
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.nameAndSignature', {
                  ns: 'global'
                })}`,
                style: 'signCell'
              },
              {
                text: `${i18next.t('ticket.pdf.nameAndSignature', {
                  ns: 'global'
                })}`,
                style: 'signCell'
              }
            ],
            [
              {
                text: requestedByUser(),
                style: 'signCell'
              },
              {
                text: clientName(),
                style: 'signCell'
              }
            ]
          ]
        },
        layout: 'borderedContainer',
        pageBreak: 'before'
      },
      ' ',
      imagesRows?.length > 0
        ? {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    text: `${i18next.t('ticket.pdf.evidence', {
                      ns: 'global'
                    })}`,
                    style: 'titleBold'
                  }
                ],
                ...chunk(imagesRows, 2).map((row) => [
                  {
                    columns: row,
                    columnGap: 20,
                    noWrap: true
                  }
                ])
              ]
            },
            layout: 'borderedContainer'
          }
        : ''
    ],
    images
  };

  return content;
};

export default getPdfTemplate;
